import { Fragment, type FocusEvent, useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNotify } from 'react-admin';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import type { COATemplate } from './TemplateDataList.tsx';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface TemplateDataDetailProps {
  template: COATemplate;
  onBack: () => void;
  onUpdated: (template: COATemplate) => void;
}

type EditableField = 'parameter' | 'testMethod' | 'uom';
type EditableFooterField = 'description' | 'preparedBy' | 'preparedByTitle' | 'preparedByDate' | 'preparedBySignature' | 'releaseBy' | 'releaseByTitle' | 'releaseByDate' | 'releaseBySignature';

export default function TemplateDataDetail({ template, onBack, onUpdated }: TemplateDataDetailProps) {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const notify = useNotify();
  const [draftTemplate, setDraftTemplate] = useState<COATemplate>(template);
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [savingItemKey, setSavingItemKey] = useState<string | null>(null);
  const [editingFooterField, setEditingFooterField] = useState<EditableFooterField | null>(null);
  const [savingFooterField, setSavingFooterField] = useState<EditableFooterField | null>(null);
  const draftTemplateRef = useRef(draftTemplate);

  useEffect(() => {
    draftTemplateRef.current = draftTemplate;
  }, [draftTemplate]);

  useEffect(() => {
    setDraftTemplate(template);
    draftTemplateRef.current = template;
    setEditingItemKey(null);
    setSavingItemKey(null);
    setEditingFooterField(null);
    setSavingFooterField(null);
  }, [template]);

  const cleanValue = (value?: string | null) => {
    const text = String(value ?? '').trim();
    return !text || text.toUpperCase() === '#VALUE!' ? '' : text;
  };

  const formatDateOnly = (value: string) => {
    const text = cleanValue(value);
    if (!text) return '';
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
    if (date.getUTCFullYear() === 1970 && date.getUTCMonth() === 0 && date.getUTCDate() === 1) return '';
    return date.toISOString().slice(0, 10);
  };

  // Detect special templates by the presence of standardValue or result on any item
  const isSpecialTemplate = draftTemplate.items.some(
    (item) => (item.standardValue != null && item.standardValue !== '') ||
              (item.result != null && item.result !== ''),
  );

  const groupedItems = draftTemplate.items.reduce<Record<string, Array<{ item: COATemplate['items'][number]; index: number }>>>((groups, item, index) => {
    const key = item.classification || 'General';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push({ item, index });
    return groups;
  }, {});

  const groupedEntries = Object.entries(groupedItems);
  const summaryFields = [
    { label: 'Product Name', value: draftTemplate.productDescription || '-' },
    { label: 'Batch Number', value: draftTemplate.batchNumber || '-' },
    { label: 'Spec Number', value: draftTemplate.specNumber || '-' },
    { label: 'Sample Submission Ref Number', value: draftTemplate.sampleSubmissionRefNumber || '-' },
    { label: 'Manufacture Date', value: formatDateOnly(draftTemplate.manufactureDate) },
    { label: 'Expiry Date', value: formatDateOnly(draftTemplate.expiryDate) },
  ];
  const preparedFields = [
    { label: 'Prepared By', value: cleanValue(draftTemplate.preparedBy) },
    { label: 'Title', value: cleanValue(draftTemplate.preparedByTitle) },
    { label: 'Date', value: formatDateOnly(draftTemplate.preparedByDate || '') },
    { label: 'Signature', value: cleanValue(draftTemplate.preparedBySignature) },
  ];
  const releasedFields = [
    { label: 'Released By', value: cleanValue(draftTemplate.releaseBy) },
    { label: 'Title', value: cleanValue(draftTemplate.releaseByTitle) },
    { label: 'Date', value: formatDateOnly(draftTemplate.releaseByDate || '') },
    { label: 'Signature', value: cleanValue(draftTemplate.releaseBySignature) },
  ];
  const description = cleanValue(draftTemplate.description);

  const updateItemField = (index: number, field: EditableField, value: string) => {
    setDraftTemplate((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
  };

  const updateFooterField = (field: EditableFooterField, value: string) => {
    setDraftTemplate((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const buildPayload = (templateValue: COATemplate) => ({
    BatchNumber: templateValue.batchNumber,
    ProductDescription: templateValue.productDescription,
    Manufacturer: templateValue.manufacturer,
    ProductType: templateValue.productType,
    CountryOfOrigin: templateValue.countryOfOrigin,
    SpecNumber: templateValue.specNumber,
    ReportNumber: templateValue.reportNumber,
    SampleSubmissionRefNumber: templateValue.sampleSubmissionRefNumber,
    SampleReceivedDate: templateValue.sampleReceivedDate || null,
    Packaging: templateValue.packaging,
    TestingPurpose: templateValue.testingPurpose,
    Client: templateValue.client,
    ManufactureDate: templateValue.manufactureDate || null,
    ExpiryDate: templateValue.expiryDate || null,
    Description: templateValue.description,
    PreparedBy: templateValue.preparedBy,
    PreparedByTitle: templateValue.preparedByTitle,
    PreparedBySignature: templateValue.preparedBySignature,
    PreparedByDate: templateValue.preparedByDate || null,
    ReleaseBy: templateValue.releaseBy,
    ReleaseByTitle: templateValue.releaseByTitle,
    ReleaseBySignature: templateValue.releaseBySignature,
    ReleaseByDate: templateValue.releaseByDate || null,
    Items: templateValue.items.map((item) => ({
      Parameter: item.parameter,
      Classification: item.classification,
      TestMethod: item.testMethod,
      Uom: item.uom,
      StandardValue: item.standardValue ?? null,
      Result: item.result ?? null,
    })),
  });

  const handleFooterFieldBlur = (field: EditableFooterField) => () => {
    if (editingFooterField !== field) return;
    setEditingFooterField(null);
    window.setTimeout(() => {
      void saveTemplate(`footer-${field}`);
    }, 0);
  };

  const saveTemplate = async (rowKey: string) => {
    if (savingItemKey || savingFooterField) return;

    const nextTemplate = draftTemplateRef.current;
    if (rowKey.startsWith('footer-')) {
      setSavingFooterField(rowKey.replace('footer-', '') as EditableFooterField);
    } else {
      setSavingItemKey(rowKey);
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/COATemplates/${nextTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(buildPayload(nextTemplate)),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const updated = await res.json().catch(() => null);
      const updatedItems = Array.isArray(updated?.items) ? updated.items : nextTemplate.items;
      const normalizedTemplate: COATemplate = {
        ...nextTemplate,
        ...(updated ?? {}),
        id: String(updated?.id ?? nextTemplate.id),
        items: updatedItems.map((item: COATemplate['items'][number], index: number) => ({
          ...nextTemplate.items[index],
          ...item,
          id: item.id ?? nextTemplate.items[index]?.id,
        })),
      };

      draftTemplateRef.current = normalizedTemplate;
      setDraftTemplate(normalizedTemplate);
      onUpdated(normalizedTemplate);
      notify('Template saved successfully', { type: 'success' });
    } catch (error: any) {
      console.error('Save template item error', error);
      if (rowKey.startsWith('footer-')) {
        const fieldName = rowKey.replace('footer-', '') as EditableFooterField;
        setEditingFooterField(fieldName);
      } else {
        setEditingItemKey(rowKey);
      }
      notify(`Save failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSavingItemKey(null);
      setSavingFooterField(null);
    }
  };

  const handleRowBlur = (index: number, rowKey: string) => (event: FocusEvent<HTMLTableRowElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (editingItemKey !== rowKey) return;

    setEditingItemKey(null);
    window.setTimeout(() => {
      void saveTemplate(rowKey);
    }, 0);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">COA Template Detail</h2>
        </div>
        <Button onClick={onBack} variant="outline" className="flex w-fit items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      <div className="border-b border-slate-100 px-5 py-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {summaryFields.map((field) => (
            <div key={field.label} className="flex min-w-0 items-center gap-2 text-sm">
              <span className="shrink-0 font-medium text-slate-500">{field.label}:</span>
              <span className="min-w-0 break-words text-slate-900">{field.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        {groupedEntries.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No template items found.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
              Double-click a row to edit. Click outside the row to save.
            </div>
            <table className="w-full table-fixed text-sm">
              {isSpecialTemplate ? (
                <colgroup>
                  <col className="w-[22%]" />
                  <col className="w-[30%]" />
                  <col className="w-[16%]" />
                  <col className="w-[22%]" />
                  <col className="w-[10%]" />
                </colgroup>
              ) : (
                <colgroup>
                  <col className="w-[46%]" />
                  <col className="w-[32%]" />
                  <col className="w-[22%]" />
                </colgroup>
              )}
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Parameter</th>
                  {isSpecialTemplate && (
                    <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Standard Value</th>
                  )}
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">UOM</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Method</th>
                  {isSpecialTemplate && (
                    <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Result</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {groupedEntries.map(([classification, items]) => (
                  <Fragment key={`group-${classification}`}>
                    <tr className="border-t-2 border-slate-200 bg-slate-700/90 text-slate-100">
                      <td colSpan={isSpecialTemplate ? 5 : 3} className="px-4 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-semibold uppercase tracking-[0.14em]">{classification}</span>
                          <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-slate-100">
                            {items.length} item{items.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {items.map(({ item, index }) => {
                      const rowKey = String(item.id ?? `${classification}-${index}`);
                      const isEditing = editingItemKey === rowKey;
                      const isSaving = savingItemKey === rowKey;

                      return (
                        <tr
                          key={rowKey}
                          className={`border-t border-slate-100 ${isEditing ? 'bg-slate-50/90' : 'hover:bg-slate-50/70'} ${isSaving ? 'opacity-70' : ''}`}
                          onDoubleClick={() => setEditingItemKey(rowKey)}
                          onBlur={handleRowBlur(index, rowKey)}
                        >
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                autoFocus
                                value={draftTemplate.items[index]?.parameter ?? ''}
                                onChange={(event) => updateItemField(index, 'parameter', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="whitespace-pre-line break-words">{item.parameter || '-'}</span>
                            )}
                          </td>
                          {isSpecialTemplate && (
                            <td className="px-4 py-1.5 align-top break-words">
                              <span className="whitespace-pre-line break-words text-slate-700">
                                {item.standardValue || '-'}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftTemplate.items[index]?.uom ?? ''}
                                onChange={(event) => updateItemField(index, 'uom', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="break-words">{item.uom || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftTemplate.items[index]?.testMethod ?? ''}
                                onChange={(event) => updateItemField(index, 'testMethod', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="whitespace-pre-line break-words">{item.testMethod || '-'}</span>
                            )}
                          </td>
                          {isSpecialTemplate && (
                            <td className="px-4 py-1.5 align-top break-words">
                              <span className="whitespace-pre-line break-words text-slate-700">
                                {item.result || '-'}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 px-5 py-5">
        <div className="mb-5">
          {editingFooterField === 'description' ? (
            <textarea
              autoFocus
              value={draftTemplate.description || ''}
              onChange={(e) => updateFooterField('description', e.target.value)}
              onBlur={handleFooterFieldBlur('description')}
              disabled={savingFooterField === 'description'}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm bg-white disabled:opacity-70"
              rows={4}
            />
          ) : (
            <div
              onDoubleClick={() => setEditingFooterField('description')}
              className="whitespace-pre-line text-sm leading-6 text-slate-900"
            >
              {description || '-'}
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3">
            {preparedFields.map((field) => {
              const mappings: Record<string, EditableFooterField> = {
                'Prepared By': 'preparedBy',
                'Title': 'preparedByTitle',
                'Date': 'preparedByDate',
                'Signature': 'preparedBySignature',
              };
              const mappedKey: EditableFooterField = mappings[field.label] || 'preparedBy';
              const isEditing = editingFooterField === mappedKey;
              const isSaving = savingFooterField === mappedKey;

              return (
                <div key={field.label} className="flex min-w-0 items-start gap-2 text-sm">
                  <span className="w-20 shrink-0 font-medium text-slate-500">{field.label}:</span>
                  {isEditing ? (
                    <Input
                      autoFocus
                      value={draftTemplate[mappedKey] || ''}
                      onChange={(e) => updateFooterField(mappedKey, e.target.value)}
                      onBlur={handleFooterFieldBlur(mappedKey)}
                      disabled={isSaving}
                      className="h-8 flex-1 bg-white"
                    />
                  ) : (
                    <span
                      onDoubleClick={() => setEditingFooterField(mappedKey)}
                      className="min-w-0 whitespace-pre-line break-words text-slate-900"
                    >
                      {field.value || '-'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            {releasedFields.map((field) => {
              const mappings: Record<string, EditableFooterField> = {
                'Released By': 'releaseBy',
                'Title': 'releaseByTitle',
                'Date': 'releaseByDate',
                'Signature': 'releaseBySignature',
              };
              const mappedKey: EditableFooterField = mappings[field.label] || 'releaseBy';
              const isEditing = editingFooterField === mappedKey;
              const isSaving = savingFooterField === mappedKey;

              return (
                <div key={field.label} className="flex min-w-0 items-start gap-2 text-sm">
                  <span className="w-20 shrink-0 font-medium text-slate-500">{field.label}:</span>
                  {isEditing ? (
                    <Input
                      autoFocus
                      value={draftTemplate[mappedKey] || ''}
                      onChange={(e) => updateFooterField(mappedKey, e.target.value)}
                      onBlur={handleFooterFieldBlur(mappedKey)}
                      disabled={isSaving}
                      className="h-8 flex-1 bg-white"
                    />
                  ) : (
                    <span
                      onDoubleClick={() => setEditingFooterField(mappedKey)}
                      className="min-w-0 whitespace-pre-line break-words text-slate-900"
                    >
                      {field.value || '-'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
